import type { Credit } from "@nlc/shared-types";
import type { SortDescriptor } from "react-aria-components";

import { useState } from "react";
import { TableBody } from "react-aria-components";

import { Badge } from "@/components/badge";
import { Cell, Column, Row, Table, TableHeader } from "@/components/table";

import { formatInvoiceDate } from "../../../components/invoices/utils/invoice-format";
import {
  formatCreditAmount,
  formatCreditIssuer,
  formatCreditReason,
  isCreditUsed,
} from "../utils/credit-format";

interface CreditsTableProps {
  readonly credits: Credit[];
}

export function CreditsTable({ credits }: CreditsTableProps) {
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>();

  return (
    <Table
      aria-label="Credits"
      onSortChange={setSortDescriptor}
      sortDescriptor={sortDescriptor}
    >
      <TableHeader className="border-y border-y-border-secondary">
        <Column
          allowsSorting
          id="account"
          isRowHeader
          minWidth={240}
          width="248fr"
        >
          Account
        </Column>
        <Column allowsSorting id="reason" minWidth={240} width="242fr">
          Reason
        </Column>
        <Column allowsSorting id="issuedBy" minWidth={124} width="124fr">
          Issued by
        </Column>
        <Column allowsSorting id="issued" minWidth={106} width="106fr">
          Issued
        </Column>
        <Column allowsSorting id="applied" minWidth={112} width="112fr">
          Applied
        </Column>
        <Column allowsSorting id="remaining" minWidth={128} width="128fr">
          Remaining
        </Column>
        <Column allowsSorting id="status" minWidth={110} width="161fr">
          Status
        </Column>
        <Column allowsSorting id="date" minWidth={127} width="127fr">
          Date
        </Column>
      </TableHeader>

      <TableBody
        items={credits}
        renderEmptyState={() => (
          <p className="px-3xl py-xl text-sm text-text-tertiary">
            No credits yet.
          </p>
        )}
      >
        {(credit) => (
          <Row id={credit.id}>
            <Cell>
              <div className="flex h-[72px] flex-col justify-center whitespace-nowrap">
                <span className="font-medium text-text-primary">
                  {credit.account.name}
                </span>
                <span>{credit.sourceInvoiceId}</span>
              </div>
            </Cell>
            <Cell>
              <span className="line-clamp-2" title={formatCreditReason(credit)}>
                {formatCreditReason(credit)}
              </span>
            </Cell>
            <Cell>
              <span className="whitespace-nowrap">
                {formatCreditIssuer(credit)}
              </span>
            </Cell>
            <Cell>{formatCreditAmount(credit.issued, credit.currency)}</Cell>
            <Cell>{formatCreditAmount(credit.applied, credit.currency)}</Cell>
            <Cell>{formatCreditAmount(credit.remaining, credit.currency)}</Cell>
            <Cell>
              <CreditStatusBadge isUsed={isCreditUsed(credit)} />
            </Cell>
            <Cell>
              <span className="whitespace-nowrap">
                {formatInvoiceDate(credit.issuedOn)}
              </span>
            </Cell>
          </Row>
        )}
      </TableBody>
    </Table>
  );
}

function CreditStatusBadge({ isUsed }: { readonly isUsed: boolean }) {
  return (
    <Badge
      className="inline-flex justify-center whitespace-nowrap"
      isRounded
      size="small"
      variant={isUsed ? "primary" : "success"}
    >
      {isUsed ? "Used" : "Active"}
    </Badge>
  );
}
